import React from 'react';
import { MenuItem } from '@mui/material';

export const ALLOCATION_TYPES = [
    { value: 'INVOICE', label: 'Rechnung' },
    { value: 'INCOME', label: 'Einnahme' },
    { value: 'EXPENSE', label: 'Ausgabe' },
    { value: 'NEW_INCOME', label: 'Neue Einnahme' },
    { value: 'NEW_EXPENSE', label: 'Neue Ausgabe' },
];

export const buildAllocationRow = (defaults) => ({
    id: `${Date.now()}-${Math.random()}`,
    allocationType: defaults?.allocationType || 'INVOICE',
    targetId: defaults?.targetId || '',
    amount: defaults?.amount || '',
    paymentDate: defaults?.paymentDate || '',
    newIncome: {
        invoiceDate: defaults?.newIncome?.invoiceDate || '',
        invoiceNumber: defaults?.newIncome?.invoiceNumber || '',
        customerName: defaults?.newIncome?.customerName || '',
        customerSearch: defaults?.newIncome?.customerSearch || defaults?.newIncome?.customerName || '',
        customerId: defaults?.newIncome?.customerId || '',
        grossAmount: defaults?.newIncome?.grossAmount || '',
        netAmount: defaults?.newIncome?.netAmount || '',
        vatAmount: defaults?.newIncome?.vatAmount || '',
        taxExcludedContribution: Boolean(defaults?.newIncome?.taxExcludedContribution),
    },
    newExpense: {
        invoiceDate: defaults?.newExpense?.invoiceDate || '',
        invoiceNumber: defaults?.newExpense?.invoiceNumber || '',
        supplierName: defaults?.newExpense?.supplierName || '',
        supplierId: defaults?.newExpense?.supplierId || '',
        grossAmount: defaults?.newExpense?.grossAmount || '',
        netAmount: defaults?.newExpense?.netAmount || '',
        vatAmount: defaults?.newExpense?.vatAmount || '',
        taxExcludedWithdrawal: Boolean(defaults?.newExpense?.taxExcludedWithdrawal),
    },
});

export const renderTargetOptions = (type, referenceData) => {
    if (type === 'INVOICE') {
        if (!referenceData.invoices.length) {
            return (
                <MenuItem value="" disabled>
                    Keine Rechnungen gefunden
                </MenuItem>
            );
        }
        return referenceData.invoices.map((invoice) => (
            <MenuItem key={invoice.id} value={invoice.id}>
                {buildInvoiceLabel(invoice)}
            </MenuItem>
        ));
    }
    if (type === 'INCOME') {
        if (!referenceData.incomes.length) {
            return (
                <MenuItem value="" disabled>
                    Keine Einnahmen gefunden
                </MenuItem>
            );
        }
        return referenceData.incomes.map((income) => (
            <MenuItem key={income.id} value={income.id}>
                {buildIncomeLabel(income)}
            </MenuItem>
        ));
    }
    if (type === 'EXPENSE') {
        if (!referenceData.expenses.length) {
            return (
                <MenuItem value="" disabled>
                    Keine Ausgaben gefunden
                </MenuItem>
            );
        }
        return referenceData.expenses.map((expense) => (
            <MenuItem key={expense.id} value={expense.id}>
                {buildExpenseLabel(expense)}
            </MenuItem>
        ));
    }
    return (
        <MenuItem value="" disabled>
            Neue Zuordnung wird erstellt
        </MenuItem>
    );
};

export const buildInvoiceLabel = (invoice) => {
    const number = invoice.invoiceNumber || 'Rechnung';
    const name = invoice.customerName || '';
    const amount = formatAmount(invoice.outstandingAmount ?? invoice.totalGross, 'EUR');
    return `${number} ${name ? `- ${name}` : ''} (${amount})`;
};

export const buildIncomeLabel = (income) => {
    const number = income.invoiceNumber || 'Einnahme';
    const name = income.customerName || '';
    const amount = formatAmount(income.netAmount ?? income.grossAmount, 'EUR');
    return `${number} ${name ? `- ${name}` : ''} (${amount})`;
};

export const buildExpenseLabel = (expense) => {
    const number = expense.invoiceNumber || 'Ausgabe';
    const name = expense.supplierName || '';
    const amount = formatAmount(expense.grossAmount ?? expense.netAmount, 'EUR');
    return `${number} ${name ? `- ${name}` : ''} (${amount})`;
};

export const resolveReferenceList = (result) => {
    if (!result || result.status !== 'fulfilled') {
        return [];
    }
    const data = result.value?.data;
    return Array.isArray(data) ? data : [];
};

export const normalizeLabel = (value) => (typeof value === 'string' ? value.trim() : '');

export const parseAmount = (value) => {
    if (value === null || value === undefined) {
        return null;
    }
    const normalized = String(value).trim().replace(',', '.');
    if (!normalized) {
        return null;
    }
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
};

export const toCurrencyString = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return '';
    }
    return String(value);
};

export const toDateInputValue = (value) => {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return date.toISOString().slice(0, 10);
};

export const formatDate = (value) => {
    if (!value) {
        return '';
    }
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
        const [year, month, day] = value.split('-');
        return `${day}.${month}.${year}`;
    }
    try {
        return new Date(value).toLocaleDateString('de-DE');
    } catch (error) {
        return String(value);
    }
};

export const formatDateTime = (value) => {
    if (!value) {
        return '';
    }
    try {
        return new Date(value).toLocaleString('de-DE');
    } catch (error) {
        return value;
    }
};

export const formatAmount = (amount, currency) => {
    if (amount == null) {
        return '';
    }
    const numeric = Number(amount);
    const code = currency || 'EUR';
    if (!Number.isFinite(numeric)) {
        return String(amount);
    }
    try {
        return new Intl.NumberFormat('de-DE', { style: 'currency', currency: code }).format(numeric);
    } catch (error) {
        return String(amount);
    }
};

export const buildIncomePayload = (data, transaction, amount, fallbackDate) => ({
    invoiceDate: data.invoiceDate || fallbackDate || null,
    invoiceNumber: data.invoiceNumber || transaction.providerTransactionId || transaction.externalId || null,
    customerName: data.customerName || transaction.name || transaction.counterpartName || null,
    grossAmount: parseAmount(data.grossAmount) ?? amount,
    netAmount: parseAmount(data.netAmount) ?? amount,
    vatAmount: parseAmount(data.vatAmount) ?? 0,
    taxExcludedContribution: Boolean(data.taxExcludedContribution),
    extractedText: transaction.description || transaction.name || transaction.counterpartName || null,
});

export const buildCustomerPayload = (name) => ({
    companyName: normalizeLabel(name),
});

export const buildExpensePayload = (data, transaction, amount, fallbackDate, paymentMethod = 'PayPal') => ({
    invoiceDate: data.invoiceDate || fallbackDate || null,
    invoiceNumber: data.invoiceNumber || transaction.providerTransactionId || transaction.externalId || null,
    supplierId: data.supplierId || null,
    supplierName: data.supplierName || transaction.name || transaction.counterpartName || null,
    grossAmount: parseAmount(data.grossAmount) ?? amount,
    netAmount: parseAmount(data.netAmount) ?? amount,
    vatAmount: parseAmount(data.vatAmount) ?? 0,
    taxExcludedWithdrawal: Boolean(data.taxExcludedWithdrawal),
    paymentMethod,
});

export const buildSupplierPayload = (name) => ({
    name: normalizeLabel(name),
});
